import React, { useEffect, useState } from 'react';
import { showToast } from '@deeptrue-frontend/ui';
import { useParams } from 'react-router';
import api from 'api';
import StoreProvider from 'components/StoreProvider/StoreProvider';
import { ProjectsReducerType } from 'store/modules/projects/projects.types';
import { ProductsReducerType } from 'store/modules/products/products.types';
import { ProjectStatus } from 'consts/statuses';
import { STATUSES } from 'components/Projects/ProjectCard';
import { useFormatMessage } from 'components/IntlProvider';

const { connect, actions, selectors } = StoreProvider;

type MapPropsType = (ownProps: { [key: string]: any }) => {
  projectId: number | string;
  organizationCodename: string;
};

interface AdditionalProps {
  projectId?: number | string;
  organizationCodename?: string;
  project: ProjectsReducerType['selected'];
  setProject: (project: ProjectsReducerType['selected']) => void;
  clearProject: () => void;
  setProduct: (product: ProductsReducerType['selected']) => void;
  clearProduct: () => void;
}

export interface WithProjectProps {
  product: ProductsReducerType['selected'];
  project: ProjectsReducerType['selected'];
  isProjectAccepted: boolean;
  isProjectArchived: boolean;
  isCollectingData: boolean;
  updateProject: () => void;
}

// TODO: Fix Types and separete prodcuts to withProduct

export const withProject =
  (mapProps?: MapPropsType) => (WrappedComponent: any) => {
    const ComponentWithProject = ({
      setProject,
      clearProject,
      setProduct,
      clearProduct,
      ...props
    }: AdditionalProps) => {
      const t = useFormatMessage();
      const [projectIsLoaded, setProjectIsLoaded] = useState(false);
      const { projectId, organizationCodename } = mapProps
        ? mapProps(props)
        : props;
      if (
        typeof projectId === 'undefined' ||
        typeof organizationCodename === 'undefined'
      ) {
        throw new Error('Missing projectId or organizationCodename in props!');
      }
      const { codename } = useParams<{ codename: string }>();

      const updateProject = () => {
        setProjectIsLoaded(false);
        api.resources
          .getProject({
            organizationCodename,
            id: projectId,
          })
          .then(({ data }: { data: ProjectsReducerType['selected'] }) => {
            Promise.all([
              api.resources.getPrivateProduct({
                orgName: organizationCodename,
                slug: data.product_slug,
              }),
              api.resources.getProjectSurveys({
                orgName: codename || organizationCodename,
                uuid: data.uuid,
              }),
            ])
              .then(([product, survey]) => {
                setProduct(product.data);
                const surveyUuid =
                  survey.data.results[0] && survey.data.results[0].uuid;
                const surveyId =
                  survey.data.results[0] && survey.data.results[0].id;
                const isVoucher = data.voucher.voucher;

                const projectTosave = {
                  ...data,
                  surveyUuid: surveyUuid,
                  surveyId: surveyId,
                  combinedTitle: {
                    projectName: data.name,
                    toolName: data.product_name,
                    status: {
                      value: ProjectStatus[data.status.value],
                      text: STATUSES[data.status.value].statusText(t),
                    },
                  },
                };
                if (isVoucher) {
                  projectTosave.json = {
                    ...projectTosave.json,
                    Bvoucher: {
                      code: data.voucher.voucher.code,
                      discount: data.voucher.voucher.discount_percent,
                      isRemoveEnabled:
                        data.status.value === ProjectStatus.OFFER_DRAFT ||
                        data.status.value === ProjectStatus.OFFER_ACCEPTED,
                      isDataCollectionStarted:
                        data.status.value !== ProjectStatus.OFFER_DRAFT &&
                        data.status.value !== ProjectStatus.OFFER_ACCEPTED,
                    },
                  };
                } else {
                  projectTosave.json = {
                    ...projectTosave.json,
                    Bvoucher: {
                      code: null,
                      discount: 0,
                      isRemoveEnabled:
                        data.status.value === ProjectStatus.OFFER_DRAFT ||
                        data.status.value === ProjectStatus.OFFER_ACCEPTED,
                      isDataCollectionStarted:
                        data.status.value !== ProjectStatus.OFFER_DRAFT &&
                        data.status.value !== ProjectStatus.OFFER_ACCEPTED,
                    },
                  };
                }

                setProject(projectTosave);
              })
              .catch(() => {
                showToast({
                  type: 'error',
                  title: t(
                    'errors.toast.missingProduct',
                    'The product no longer exists. Delete the project or contact with the administrator.'
                  ),
                });
                clearProduct();
              })
              .finally(() => {
                setProjectIsLoaded(true);
              });
          });
      };

      useEffect(() => {
        if (props.project.id !== Number(projectId)) {
          if (props.project.id !== -1) {
            clearProject();
            clearProduct();
          }
          updateProject();
        }
        // eslint-disable-next-line
      }, []);

      return (
        <WrappedComponent
          {...props}
          projectIsLoaded={projectIsLoaded}
          isProjectAccepted={
            ProjectStatus.OFFER_ACCEPTED === props.project.status.value
          }
          isProjectArchived={
            ProjectStatus.OFFER_ARCHIVED === props.project.status.value
          }
          isCollectingData={
            ProjectStatus.COLLECTING_DATA === props.project.status.value
          }
          updateProject={updateProject}
        />
      );
    };
    return connect(
      (state) => ({
        project: selectors.projects.selected(state),
        product: selectors.products.selected(state),
      }),
      {
        setProduct: actions.products.setProduct,
        clearProduct: actions.products.clearBriefing,
        setProject: actions.projects.setProject,
        clearProject: actions.projects.clearProject,
      }
    )(ComponentWithProject);
  };
